import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Briefcase, CalendarDays, CheckCircle2, ListChecks, Gift, Info } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { ApplicationForm } from "@/components/ApplicationForm";

const Section = ({ icon: Icon, title, items }) => (
  items && items.length ? (
    <div className="mt-8">
      <h2 className="flex items-center gap-2 text-lg md:text-lg font-bold text-gray-900"><Icon className="w-5 h-5 text-[#D62B3A]" /> {title}</h2>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 text-[#D62B3A] shrink-0 mt-0.5" /><span>{it}</span></li>
        ))}
      </ul>
    </div>
  ) : null
);

export default function KarriereDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [branches, setBranches] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/careers/jobs/${slug}`).then((r) => setJob(r.data)).catch(() => setNotFound(true));
    api.get("/careers/meta").then((r) => setBranches(r.data.branches || []));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">Stelle nicht gefunden</h1>
        <p className="mt-2 text-gray-600">Diese Stelle ist nicht mehr verfügbar.</p>
        <Link to="/karriere" className="inline-block mt-6 text-[#D62B3A] font-semibold">← Zu allen Stellen</Link>
      </div>
    );
  }
  if (!job) return <div className="max-w-3xl mx-auto px-4 py-24 text-gray-400">Laden…</div>;

  return (
    <div>
      <Seo title={job.seo_title || `${job.title} – HELIUM Catering`} description={job.seo_description} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <Link to="/karriere" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#D62B3A]" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> Alle Stellen
        </Link>

        <div className="mt-6 bg-white rounded-2xl border border-gray-100 p-6 lg:p-10 shadow-sm">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D62B3A] uppercase tracking-wide"><Briefcase className="w-4 h-4" /> {job.area}</span>
          <h1 className="mt-2 text-3xl lg:text-4xl font-extrabold" data-testid="detail-title">{job.title}</h1>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#D62B3A]" /> {job.branch_name}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#D62B3A]" /> {job.employment_type}</span>
            <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#D62B3A]" /> Eintritt: {job.start_date}</span>
            {job.deadline && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#D62B3A]" /> Bewerbungsfrist: {job.deadline}</span>}
          </div>

          {job.intro && <p className="mt-6 text-gray-700 text-lg leading-relaxed">{job.intro}</p>}

          <Section icon={ListChecks} title="Deine Aufgaben" items={job.tasks} />
          <Section icon={CheckCircle2} title="Dein Profil" items={job.requirements} />
          <Section icon={Gift} title="Wir bieten" items={job.benefits} />

          {job.additional_info && (
            <div className="mt-8 bg-[#F9F8F6] rounded-xl p-5">
              <h2 className="flex items-center gap-2 font-bold text-gray-900"><Info className="w-5 h-5 text-[#D62B3A]" /> Zusätzliche Hinweise</h2>
              <p className="mt-2 text-gray-700 whitespace-pre-line">{job.additional_info}</p>
            </div>
          )}

          <a href="#bewerben" className="mt-8 inline-block px-8 py-4 rounded-xl bg-[#D62B3A] hover:bg-[#b8232f] text-white font-semibold text-lg transition-colors" data-testid="detail-apply-cta">
            Jetzt bewerben
          </a>
        </div>

        <div id="bewerben" className="mt-8 bg-white rounded-2xl border border-gray-100 p-6 lg:p-10 shadow-sm scroll-mt-24">
          <h2 className="text-2xl font-bold">Jetzt auf <span className="text-[#D62B3A]">{job.title}</span> bewerben</h2>
          <p className="mt-1 text-gray-500">Standort {job.branch_name}. Wir freuen uns auf deine Bewerbung!</p>
          <div className="mt-6">
            <ApplicationForm job={job} branches={branches} />
          </div>
        </div>
      </div>
    </div>
  );
}
